import ButtonT from '@components/tailwind-components/Button/ButtonT';
import InputT from '@components/tailwind-components/Input/InputT';
import InputWithLabelT from '@components/tailwind-components/Input/InputWithLabelT';
import RadioInputT from '@components/tailwind-components/Input/RadioInputT';
import SelectInputT from '@components/tailwind-components/Input/SelectInputT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import type {
  DataSourceDataDTO,
  DataSourceDetailsDTO,
} from '@conversed/rest/src/contracts/data-sources/data-sources.dto';
import { toastErrorTranslated } from '@helpers/toast';
import { useDataSources } from '@hooks/dataSources/useDataSources';
import { useFolderSelection } from '@hooks/dataSources/useFolderSelection';
import { usePlatformFeatures } from '@hooks/usePlatformFeatures';
import { format, startOfDay } from 'date-fns';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useModal } from '../../hooks/modal/useModal';
import { ExpiryDatePickerSection } from './sections/ExpiryDatePickerSection';

type LabelMode = 'select' | 'new';
const MAX_LABEL_LENGTH = 50;

type Props = {
  selectedDataSource?: DataSourceDetailsDTO;
  onSubmit: (data: DataSourceDataDTO) => void;
  mode: 'edit'; // only edit mode is supported currently, there was 'upload' before
  dataSources: DataSourceDetailsDTO[];
};

export const DataSourceDataModal: React.FC<Props> = ({
  selectedDataSource,
  onSubmit,
  dataSources,
}) => {
  const { t } = useTranslation();
  const { hasAIKnowledgeHubV2 } = usePlatformFeatures();
  const { allFiles } = useDataSources();
  const { isTargetTemporary } = useFolderSelection(
    selectedDataSource?.folderId || undefined,
  );
  const { handleSubmit, control } = useForm<DataSourceDataDTO>({
    defaultValues: {
      name: selectedDataSource?.name || '',
      documentType: selectedDataSource?.documentType || undefined,
    },
    shouldUseNativeValidation: true,
  });
  const [isDuplicateName, setIsDuplicateName] = useState(false);
  const [expiryDate, setExpiryDate] = useState<Date | null>(
    selectedDataSource?.expiryDate
      ? new Date(selectedDataSource.expiryDate)
      : null,
  );
  const { hideModal } = useModal();

  // Extract existing labels from all data sources for the label select input
  const existingLabels = useMemo(
    () =>
      Array.from(
        new Set(
          allFiles
            .map((dataSource) => dataSource.label)
            .filter((label): label is string => !!label),
        ),
      ),
    [allFiles],
  );

  // Label editing state
  const initialLabelMode = useMemo<LabelMode>(() => {
    if (
      selectedDataSource?.label &&
      !existingLabels.includes(selectedDataSource.label)
    ) {
      return 'new';
    }
    return 'select';
  }, [selectedDataSource?.label, existingLabels]);

  const [labelMode, setLabelMode] = useState<LabelMode>(initialLabelMode);
  const [selectedLabel, setSelectedLabel] = useState<string>(
    selectedDataSource?.label || '',
  );
  const [newLabel, setNewLabel] = useState<string>(
    initialLabelMode === 'new' ? selectedDataSource?.label || '' : '',
  );
  const [newLabelError, setNewLabelError] = useState('');

  const labelSelectOptions = useMemo(
    () => [
      { value: '', description: t('data-sources.edit.no-label') },
      ...existingLabels.map((existingLabel) => ({
        value: existingLabel,
        description: existingLabel,
      })),
    ],
    [existingLabels, t],
  );

  const validateNewLabel = useCallback(
    (value: string): string => {
      const trimmed = value.trim();
      if (value.length > 0 && trimmed.length === 0) {
        return t('data-sources.sidebar.label-error.whitespace-only');
      }
      if (trimmed.length > MAX_LABEL_LENGTH) {
        return t('data-sources.sidebar.label-error.max-length', {
          max: MAX_LABEL_LENGTH,
        });
      }
      return '';
    },
    [t],
  );

  const handleNewLabelChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newLabelValue = e.target.value;
      setNewLabel(newLabelValue);
      setNewLabelError(validateNewLabel(newLabelValue));
    },
    [validateNewLabel],
  );

  const hideAndSubmit = useCallback(
    async (data: DataSourceDataDTO) => {
      setIsDuplicateName(false);
      // if the user didn't provide a custom name, use the file name
      if (!data.name?.trim()) {
        data.name = data?.data?.name
          ? data.data.name
          : selectedDataSource?.name || '';
      }

      const isFileNameAlreadyExists = dataSources.some(
        (dataSource) =>
          dataSource.name === data.name &&
          dataSource._id !== selectedDataSource?._id &&
          dataSource.folderId === selectedDataSource?.folderId,
      );

      if (isFileNameAlreadyExists) {
        toastErrorTranslated(
          'data-sources.upload.duplicate-name-toast-message',
          {
            fileName: data.name,
          },
        );
        setIsDuplicateName(true);

        return;
      }

      if (hasAIKnowledgeHubV2) {
        if (labelMode === 'new') {
          const error = validateNewLabel(newLabel);
          if (error) {
            setNewLabelError(error);
            return;
          }
          data.label = newLabel.trim();
        } else {
          data.label = selectedLabel;
        }

        if (isTargetTemporary && !expiryDate) {
          toastErrorTranslated('data-sources.expiry.required-error');
          return;
        }

        if (
          isTargetTemporary &&
          expiryDate &&
          expiryDate < startOfDay(new Date())
        ) {
          toastErrorTranslated('data-sources.expiry.past-date-error');
          return;
        }

        data.expiryDate =
          isTargetTemporary && expiryDate
            ? format(expiryDate, 'yyyy-MM-dd')
            : undefined;
      }

      await onSubmit(data);
      hideModal();
    },
    [
      dataSources,
      hideModal,
      onSubmit,
      selectedDataSource,
      hasAIKnowledgeHubV2,
      labelMode,
      newLabel,
      selectedLabel,
      validateNewLabel,
      expiryDate,
      isTargetTemporary,
    ],
  );

  return (
    <form
      className='tailwind-modal-form'
      onSubmit={handleSubmit(hideAndSubmit)}>
      <div className='tailwind-modal-body'>
        <div className='grid grid-cols-1 gap-x-6 gap-y-2'>
          <div className='col-span-1 relative'>
            <InputWithLabelT
              type='text'
              field='name'
              placeholder={t('data-sources.field.name')}
              label={t('data-sources.field.name')}
              control={control}
              options={{
                onChange: () => {
                  setIsDuplicateName(false);
                },
              }}
              error={
                isDuplicateName
                  ? t('data-sources.upload.duplicate-name-input-message')
                  : ''
              }
            />
          </div>
          {hasAIKnowledgeHubV2 && (
            <div className='col-span-1'>
              <LabelT text={t('data-sources.edit.label-section')} />
              <div className='flex flex-col gap-1 mt-1'>
                <div className='flex flex-col gap-x-5 sm:flex-row sm:items-center'>
                  <RadioInputT
                    id='labelMode-select'
                    name='labelMode'
                    label={t('data-sources.edit.select-existing')}
                    checked={labelMode === 'select'}
                    onChange={() => setLabelMode('select')}
                    className='my-0'
                    labelClassName='!text-xs'
                  />
                  <RadioInputT
                    id='labelMode-new'
                    name='labelMode'
                    label={t('data-sources.edit.enter-new')}
                    checked={labelMode === 'new'}
                    onChange={() => setLabelMode('new')}
                    className='my-0'
                    labelClassName='!text-xs'
                  />
                </div>
                {labelMode === 'select' && (
                  <SelectInputT
                    values={labelSelectOptions}
                    value={selectedLabel}
                    onChange={(e) =>
                      setSelectedLabel((e.target as HTMLSelectElement).value)
                    }
                  />
                )}

                {labelMode === 'new' && (
                  <InputT
                    type='text'
                    parentClassName='!mb-0'
                    placeholder={t('data-sources.edit.new-label-placeholder')}
                    // maxLength={MAX_LABEL_LENGTH}
                    error={newLabelError}
                    options={{
                      value: newLabel,
                      onChange: handleNewLabelChange,
                    }}
                  />
                )}
              </div>
            </div>
          )}
          {hasAIKnowledgeHubV2 && (
            <div className='col-span-1'>
              <ExpiryDatePickerSection
                id='modal-expiry-date'
                expiryDate={expiryDate}
                onChange={setExpiryDate}
                isTargetTemporary={isTargetTemporary}
              />
            </div>
          )}
        </div>
      </div>
      <div className='tailwind-modal-footer'>
        <ButtonT
          onClick={() => hideModal()}
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT type='submit' styling='primary' size='xs' rounding='md'>
          {t('general.buttons.save')}
        </ButtonT>
      </div>
    </form>
  );
};

export default DataSourceDataModal;
